import cloneDeep from 'lodash/cloneDeep'

import type {Dependency, Jobs} from '../../../../types'
import schemaJson from '../schema.json'

type DependencyType = {
  type: 'string'
  enum: string[]
}

type PropertirsType = Record<
  string,
  {
    additionalProperties: boolean
    properties: {
      files: {
        type: 'array'
        items: {
          type: 'string'
          enum: string[]
        }
      }
    }
  }
>

type DependencyWithFilesType = {
  type: 'object'
  additionalProperties: boolean
  properties: PropertirsType
}

const hasCyclicalDependencies = (dependencies: Dependency[], jobName: string) =>
  dependencies.find(dependence => {
    if (typeof dependence === 'object') {
      return dependence[jobName] != null
    } else if (typeof dependence === 'string') {
      return dependence === jobName
    }
    return false
  }) != null

const getAllowedDependencies = (jobs: Jobs, jobName: string) =>
  Object.keys(jobs).filter(
    name => name !== jobName && !hasCyclicalDependencies(jobs[name]?.dependencies || [], jobName),
  )

function getJsonSchemaJobDependencies(jobs: Jobs, jobName: string) {
  const clonedJobDependenciesSchema = cloneDeep(schemaJson.definitions.job.properties.dependencies)
  const allowedDependencies = getAllowedDependencies(jobs, jobName)

  // It is necessary to extend the type since the scheme describes general rules
  const case1 = clonedJobDependenciesSchema.items.anyOf[0] as DependencyType
  case1.enum = allowedDependencies.filter(name => {
    const dependencies = jobs[jobName]?.dependencies || []
    return !dependencies.find(
      dependence => typeof dependence === 'object' && dependence[name] != null,
    )
  })

  // It is necessary to extend the type since the scheme describes general rules
  const case2 = clonedJobDependenciesSchema.items.anyOf[1] as DependencyWithFilesType
  case2.additionalProperties = false
  case2.properties = allowedDependencies.reduce<PropertirsType>((acc2, name) => {
    const availableArtifacts = jobs[name]?.['files-publication'] || []
    return {
      ...acc2,
      [name]: {
        additionalProperties: false,
        properties: {
          files: {
            type: 'array',
            items: {
              type: 'string',
              enum: availableArtifacts,
            },
          },
        },
      },
    }
  }, {})

  return clonedJobDependenciesSchema
}

export default getJsonSchemaJobDependencies
